import React from 'react';
import {
  Body,
  Button,
  Checkbox,
  Dropdown,
  Flex,
  Loader,
  Menu,
  Table,
  TableColumn,
} from '@cognite/cogs.js';
import styled from 'styled-components/macro';
import { FailedInstance } from '../consistencyReportTypes';
import { useFailedInstancesTable } from './useFailedInstancesTable';

type FailedInstancesTableProps = {
  instances: FailedInstance[];
  loading: boolean;
};

const formatCellValue = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

export const FailedInstancesTable = ({
  instances,
  loading,
}: FailedInstancesTableProps) => {
  const { orderedKeys, hiddenColumns, toggleColumn, visibleKeys } =
    useFailedInstancesTable(instances);

  if (loading) {
    return <Loader />;
  }

  if (instances.length === 0) {
    return <Body size="small">No failed instances.</Body>;
  }

  // Use a function accessor + explicit id to avoid react-table's dot-notation
  // path traversal, which would misinterpret "Filter.field" as nested access.
  const columns: TableColumn<FailedInstance>[] = visibleKeys.map((col) => ({
    Header: col,
    id: col,
    accessor: (row: FailedInstance) => row[col],
    Cell: ({ value }: { value: unknown }) => <>{formatCellValue(value)}</>,
  }));

  return (
    <>
      <Toolbar justifyContent="flex-end" alignItems="center" gap={8}>
        <Dropdown
          content={
            <ColumnsMenu>
              {orderedKeys.map((key) => (
                <Menu.Item key={key}>
                  <Checkbox
                    name={key}
                    checked={!hiddenColumns.has(key)}
                    onChange={() => toggleColumn(key)}
                  >
                    {key}
                  </Checkbox>
                </Menu.Item>
              ))}
            </ColumnsMenu>
          }
        >
          <Button
            icon="Settings"
            iconPlacement="right"
            aria-label="Choose columns"
          >
            Columns ({visibleKeys.length}/{orderedKeys.length})
          </Button>
        </Dropdown>
      </Toolbar>

      <TableWrapper>
        <Table<FailedInstance>
          columns={columns}
          dataSource={instances}
          pagination
          rowKey={(data: FailedInstance, index?: number) =>
            String(data.externalId ?? index)
          }
        />
      </TableWrapper>
    </>
  );
};

const Toolbar = styled(Flex)`
  margin-top: 0.75rem;
`;

const ColumnsMenu = styled(Menu)`
  max-height: 60vh;
  overflow: auto;
  min-width: 200px;
`;

const TableWrapper = styled.div`
  margin-top: 0.5rem;
  border: 1px solid var(--cogs-bg-control--disabled);
  border-radius: 6px;
  overflow-x: auto;

  td {
    max-width: 300px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;
